/**
 * The theme CATALOGUE: core types, the stock light/dark colour maps and the 24 editable roles.
 *
 * theme-customization A1, split out of `theme.ts` at the head of A2a (behaviour-preserving —
 * every export below is re-exported by `theme.js`, so no importer changed). This module is the
 * DATA half of the contract and imports nothing else in this package; `theme-schema.ts` builds
 * on it and `theme.ts` resolves and validates with it.
 *
 * Nothing here imports from `apps/web`; the stock LIGHT literals below are a COPY of the two
 * portal stylesheets, and `apps/web/src/styles/__tests__/theme-pair-coverage.test.ts` compares
 * the copy against the actual CSS so a divergence reddens on the web side rather than silently
 * shipping.
 *
 * Provenance of the light values (recorded so the copy can be re-derived, not re-guessed):
 *   - `triplet` tokens are `apps/web/src/styles/globals.css:35-86`'s shadcn channel triplets,
 *     converted with the same algorithm as `token-sources.ts`'s `hslTripletToHex`
 *     (`Math.round((v + m0) * 255)` per channel).
 *   - `hex-pair` tokens are `apps/web/src/styles/tokens.css`'s hex declarations, uppercased.
 *   - 19 triplet + 112 hex-pair = 131 tokens, which is exactly the `declared.length` that
 *     `apps/web/src/styles/__tests__/contrast-registry.test.ts` pins after A0.
 *
 * Dark values come from the plan's role catalogue (dark column) and its "Dark partners"
 * table; every token the tables do not name retains its light value. Dark is NOT exposed or
 * accepted by delivery A — B1 validates it before B2 publishes the capability.
 */

// ─── Core types ──────────────────────────────────────────────────────────────

export type ThemeMode = 'light' | 'dark';
export type ThemePreference = 'SYSTEM' | 'LIGHT' | 'DARK';

/** A validated six-digit hex colour, not arbitrary CSS. */
export type HexColour = string;

export type ThemeTokenShape = 'hex-pair' | 'triplet';

/** The exact 24 editable role IDs. */
export type ThemeRoleId =
  | 'accent-admin'
  | 'accent-client'
  | 'accent-influencer'
  | 'accent-agent'
  | 'interactive'
  | 'body-text'
  | 'muted-text'
  | 'raised-surface'
  | 'destructive'
  | 'hairline'
  | 'control-boundary'
  | 'highlight'
  | 'page-canvas'
  | 'card-surface'
  | 'chrome'
  | 'chrome-gradient-end'
  | 'on-chrome'
  | 'hairline-strong'
  | 'hairline-hover'
  | 'hairline-soft'
  | 'text-strong'
  | 'text-secondary'
  | 'text-decorative'
  | 'content-secondary';

export type RoleOverrides = Partial<Record<ThemeRoleId, string>>;
export type ThemeOverrides = { light: RoleOverrides; dark: RoleOverrides };

export type ThemeSnapshot = {
  ownerUserId: string;
  preference: ThemePreference;
  overrides: ThemeOverrides;
  revision: number;
  supportedModes: ThemePreference[];
};

export type ThemeSaveInput = { overrides: ThemeOverrides; revision: number };
export type ThemeSaveResult = { overrides: ThemeOverrides; revision: number };
export type PreferenceResult = { ownerUserId: string; preference: ThemePreference };

export type ThemeFailure = {
  pairId: string;
  mode: ThemeMode;
  roleIds: ThemeRoleId[];
  foreground: string;
  background: string;
  ratio: number;
  minimum: number;
};

export type ThemeValidation =
  | { valid: true; failures: [] }
  | { valid: false; failures: ThemeFailure[] };

// ─── Stock LIGHT: the 19 globals.css triplet tokens ──────────────────────────

/**
 * `globals.css` `:root`, converted from HSL channel triplets. These 19 names are exactly the
 * set whose CSS shape is a BARE TRIPLET; every other token below is a hex plus a generated
 * `-hsl` companion. `THEME_TOKEN_SHAPES` is derived from this list, so the serializer on the
 * web side cannot write the wrong shape for a token.
 */
const TRIPLET_TOKEN_NAMES = [
  '--background',
  '--foreground',
  '--card',
  '--card-foreground',
  '--popover',
  '--popover-foreground',
  '--primary',
  '--primary-foreground',
  '--secondary',
  '--secondary-foreground',
  '--muted',
  '--muted-foreground',
  '--accent',
  '--accent-foreground',
  '--destructive',
  '--destructive-foreground',
  '--border',
  '--input',
  '--ring',
] as const;

const STOCK_LIGHT = {
  // ── globals.css: the shadcn semantic set (19, shape `triplet`) ────────────
  '--background': '#F1F1F1',
  '--foreground': '#333333',
  '--card': '#FFFFFF',
  '--card-foreground': '#333333',
  '--popover': '#FFFFFF',
  '--popover-foreground': '#333333',
  '--primary': '#000000',
  '--primary-foreground': '#FFFFFF',
  '--secondary': '#E8E8E8',
  '--secondary-foreground': '#333333',
  '--muted': '#F1F5F9',
  '--muted-foreground': '#595959',
  '--accent': '#F1F5F9',
  '--accent-foreground': '#0F172A',
  '--destructive': '#A51D2D',
  '--destructive-foreground': '#FFFFFF',
  '--border': '#DDDDDD',
  '--input': '#767676',
  '--ring': '#000000',

  // ── tokens.css: brand + AI (10) ──────────────────────────────────────────
  '--ss-black': '#1A1A1A',
  '--ss-yellow': '#FFD60A',
  '--ss-yellow-light': '#FFE066',
  '--ss-pink': '#FF6B9D',
  '--ss-pink-light': '#FF8FAB',
  '--ss-blue': '#4DABF7',
  '--ss-blue-light': '#74C0FC',
  '--ss-purple': '#A78BFA',
  '--ss-purple-light': '#818CF8',
  '--ai-purple': '#8B5CF6',

  // ── tokens.css: portal chart series (8, code-owned) ───────────────────────
  '--chart-1': '#000000',
  '--chart-2': '#D4FF00',
  '--chart-3': '#FF2D9C',
  '--chart-4': '#00D4FF',
  '--chart-5': '#8D8D8D',
  '--chart-6': '#C2116C',
  '--chart-7': '#0094B3',
  '--chart-8': '#8FB300',

  // ── tokens.css: shared avatar hash palette (16, code-owned) ───────────────
  '--avatar-hash-1-bg': '#DBEAFE',
  '--avatar-hash-1-text': '#1D4ED8',
  '--avatar-hash-2-bg': '#D1FAE5',
  '--avatar-hash-2-text': '#065F46',
  '--avatar-hash-3-bg': '#FEF3C7',
  '--avatar-hash-3-text': '#92400E',
  '--avatar-hash-4-bg': '#FCE7F3',
  '--avatar-hash-4-text': '#9D174D',
  '--avatar-hash-5-bg': '#EDE9FE',
  '--avatar-hash-5-text': '#5B21B6',
  '--avatar-hash-6-bg': '#FFEDD5',
  '--avatar-hash-6-text': '#9A3412',
  '--avatar-hash-7-bg': '#F3F4F6',
  '--avatar-hash-7-text': '#374151',
  '--avatar-hash-8-bg': '#CFFAFE',
  '--avatar-hash-8-text': '#155E75',

  // ── tokens.css: report avatar hash palette (12, report-owned) ─────────────
  '--report-avatar-1-bg': '#DBEAFE',
  '--report-avatar-1-text': '#1D4ED8',
  '--report-avatar-2-bg': '#F5F3FF',
  '--report-avatar-2-text': '#7C3AED',
  '--report-avatar-3-bg': '#D1FAE5',
  '--report-avatar-3-text': '#047857',
  '--report-avatar-4-bg': '#FFEDD5',
  '--report-avatar-4-text': '#9A3412',
  '--report-avatar-5-bg': '#FCE7F3',
  '--report-avatar-5-text': '#BE185D',
  '--report-avatar-6-bg': '#E0F2FE',
  '--report-avatar-6-text': '#0369A1',

  // ── tokens.css: tags, portal foregrounds and identity marks (14) ──────────
  '--tag-pink-bg': '#FCE7F3',
  '--tag-pink-text': '#BE185D',
  '--tag-cyan-bg': '#CFFAFE',
  '--tag-cyan-text': '#0E7490',
  '--portal-admin-foreground': '#854D0E',
  '--portal-client-foreground': '#1864AB',
  '--portal-influencer-foreground': '#9D174D',
  '--portal-agent-foreground': '#5B21B6',
  '--submission-avatar-start': '#C6B2FF',
  '--submission-avatar-end': '#A78BFA',
  '--feedback-influencer-accent': '#DB2777',
  '--post-source-later': '#1E6DB8',
  '--post-source-approval': '#7A1F45',
  '--instagram-soft': '#EC4899',

  // ── tokens.css: dark application chrome (9) ───────────────────────────────
  '--chrome-bg': '#1E293B',
  '--chrome-bg-hover': '#334155',
  '--chrome-bg-hover-soft': '#272E38',
  '--chrome-bg-active': '#2A3549',
  '--chrome-text': '#E2E8F0',
  '--chrome-text-muted': '#94A3B8',
  '--chrome-border': '#334155',
  '--chrome-gradient-end': '#2D2D35',
  '--on-chrome': '#FFFFFF',

  // ── tokens.css: fixed media controls (2, code-owned in BOTH modes) ────────
  '--fixed-white': '#FFFFFF',
  '--fixed-dark': '#1A1A1A',

  // ── tokens.css: A2a code-owned semantic aliases (9) ───────────────────────
  // Nine roles that used to be a shared value with an EDITABLE token, split out so a palette
  // edit cannot reach a pair whose other operand it does not move. Every light value is
  // byte-identical to the token it splits away from, which is why A2a repaints nothing.
  // `--switch-checked` (light) and `--switch-checked-boundary` (both modes) are DERIVED by
  // `resolveTheme` from the resolved `--primary` / `--input`; the values here are their stock
  // light resolutions, present so the stock map is complete and matches the stylesheet.
  '--on-accent': '#111827',
  '--on-accent-initials': '#1A1A1A',
  '--status-approved-readable': '#0E6027',
  '--status-changes-readable': '#9A3412',
  '--status-rejected-text': '#A2191F',
  '--ai-text': '#6D28D9',
  '--switch-checked': '#000000',
  '--switch-checked-boundary': '#000000',
  '--chart-boundary': '#000000',

  // ── tokens.css: chart canvas ink (1, code-owned) ─────────────────────────
  // The SECOND role `--chart-boundary` was carrying. A boundary is stroked ON a series fill, so
  // black in both modes is correct for it. This one is stroked on the CANVAS — the area curve
  // substitute, the donut leader, the sentiment leader — so its ground is `--card` / `--bg-card`
  // and it has to follow the mode. Same light value, so nothing repaints in light; it is its own
  // token for the reason A2a split the boundary out, namely that a series colour moving must not
  // move a band that is not a series.
  '--chart-ink': '#000000',

  // ── tokens.css: highlight (2) ────────────────────────────────────────────
  '--highlight': '#D4FF00',
  '--highlight-subtle': '#F7FFCC',

  // ── tokens.css: surfaces (3) ─────────────────────────────────────────────
  '--bg-light': '#F1F1F1',
  '--bg-card': '#FFFFFF',
  '--bg-surface-light': '#F8F9FC',

  // ── tokens.css: hairlines (3) ────────────────────────────────────────────
  '--brand-border': '#D5D8DF',
  '--brand-border-hover': '#C4C8D1',
  '--brand-border-soft': '#F1F3F7',

  // ── tokens.css: the text ladder (6) ──────────────────────────────────────
  '--text-primary': '#111827',
  '--text-body': '#374151',
  '--text-secondary': '#595959',
  '--text-muted': '#9CA3AF',
  '--content-secondary': '#4B5563',
  '--content-muted': '#595959',

  // ── tokens.css: status families (23) ─────────────────────────────────────
  '--status-draft': '#6B7280',
  '--status-draft-text': '#4B5563',
  '--status-draft-bg': '#F3F4F6',
  '--status-pending': '#F1C21B',
  '--status-pending-text': '#854D0E',
  '--status-pending-bg': '#FCF4D6',
  '--status-approved': '#24A148',
  '--status-approved-text': '#0E6027',
  '--status-approved-bg': '#DEFBE6',
  '--status-changes': '#F97316',
  '--status-changes-text': '#9A3412',
  '--status-changes-bg': '#FFEDD5',
  '--status-review': '#0043CE',
  '--status-review-text': '#0043CE',
  '--status-review-bg': '#EDF5FF',
  '--status-rejected': '#A2191F',
  '--status-rejected-bg': '#FFF1F1',
  '--status-abandoned': '#64748B',
  '--status-abandoned-text': '#475569',
  '--status-abandoned-bg': '#F1F5F9',
  '--status-backup': '#475569',
  '--status-backup-text': '#334155',
  '--status-backup-bg': '#E2E8F0',

  // ── tokens.css: consumer-intelligence report palette (4) ─────────────────
  '--report-ink': '#0F172A',
  '--report-subtle': '#94A3B8',
  '--report-tint': '#F5F3FF',
  '--report-accent': '#6D28D9',
} as const satisfies Record<string, HexColour>;

/** The finite union of every editable and code-owned colour token in the stock maps. */
export type ThemeTokenId = keyof typeof STOCK_LIGHT;

export type ResolvedTheme = Readonly<Record<ThemeTokenId, HexColour>>;

/**
 * Dark values that differ from light. Sources: the plan's 24-role catalogue (dark column) and
 * its "Dark partners below complete the editable table" table. "Unlisted code-owned tokens
 * retain their light values" is implemented by the spread below rather than by re-listing them.
 *
 * NOT VALIDATED BY DELIVERY A. `validateTheme` is only ever called with `['light']` until B1
 * proves the complete dark instrument; `A`'s API refuses new dark edits outright.
 */
const STOCK_DARK_DIFFERENCES = {
  // Role catalogue, dark column.
  '--primary': '#FFFFFF',
  '--ring': '#FFFFFF',
  '--foreground': '#CCCCCC',
  '--card-foreground': '#CCCCCC',
  '--popover-foreground': '#CCCCCC',
  '--muted-foreground': '#AAAAAA',
  '--secondary': '#262626',
  '--destructive': '#F87171',
  '--border': '#333333',
  '--input': '#858585',
  '--bg-light': '#000000',
  '--background': '#000000',
  '--bg-card': '#1A1A1A',
  '--card': '#1A1A1A',
  '--popover': '#1A1A1A',
  '--brand-border': '#444444',
  '--brand-border-hover': '#555555',
  '--brand-border-soft': '#262626',
  '--text-primary': '#FFFFFF',
  '--text-secondary': '#AAAAAA',
  '--text-muted': '#767676',
  '--content-secondary': '#CCCCCC',

  // Dark partners table.
  '--primary-foreground': '#111827',
  '--destructive-foreground': '#111827',
  '--secondary-foreground': '#CCCCCC',
  '--accent-foreground': '#CCCCCC',
  '--text-body': '#CCCCCC',
  '--muted': '#262626',
  '--accent': '#262626',
  '--bg-surface-light': '#262626',
  '--content-muted': '#AAAAAA',
  '--highlight-subtle': '#2A2E0C',
  '--status-draft': '#AAAAAA',
  '--status-draft-text': '#CCCCCC',
  '--status-draft-bg': '#262626',
  '--status-pending': '#F1C21B',
  '--status-pending-text': '#FBBF24',
  '--status-pending-bg': '#2E220A',
  '--status-approved': '#4ADE80',
  '--status-approved-bg': '#12291B',
  '--status-changes': '#FB923C',
  '--status-changes-bg': '#331E13',
  '--status-review-text': '#93C5FD',
  '--status-review-bg': '#10263A',
  '--status-rejected-bg': '#331617',
  '--status-abandoned': '#94A3B8',
  '--status-abandoned-text': '#CBD5E1',
  '--status-abandoned-bg': '#1E293B',
  '--status-backup': '#CBD5E1',
  '--status-backup-text': '#E2E8F0',
  '--status-backup-bg': '#334155',
  // B1 CORRECTION, under the plan's "if a stock partner fails an actual consumer, correct its
  // value or specific semantic split in B1, record the measured change here, and retain the
  // case/floor". The influencer role chip (`FeedbackComment.tsx` `bg-tag-pink-background`) is the
  // only chip of the four whose surface was an UNLISTED token, so it kept the light tint while
  // its foreground and its identity boundary both moved to the dark pastel — two rendered pairs
  // of light-on-light. The tint moves instead of the foreground because the foreground has other
  // dark-card consumers (`NotificationPageShell.tsx`, `NotificationItem.tsx`, `UsersPage.tsx`)
  // that need the pastel. Its own self-contained pair member moves with it so the tag chip in
  // `CreatorsModule.tsx` / `ShortlistSummary.tsx` stays a measured pair. Ratios: the plan table.
  '--tag-pink-bg': '#331623',
  '--tag-pink-text': '#F9A8D4',
  '--portal-admin-foreground': '#FBBF24',
  '--portal-client-foreground': '#93C5FD',
  '--portal-influencer-foreground': '#F9A8D4',
  '--portal-agent-foreground': '#C4B5FD',
  '--feedback-influencer-accent': '#F9A8D4',
  '--post-source-later': '#93C5FD',
  '--post-source-approval': '#FFFFFF',
  '--ai-purple': '#A78BFA',
  '--chart-1': '#FFFFFF',
  '--chart-6': '#FF70B5',

  // A2a semantic splits, dark column. Only the values the plan states as a LITERAL hex are
  // here; `--on-accent` / `--on-accent-initials` are identical in both modes and
  // `--switch-checked-boundary` is derived, so none of those three appears.
  '--status-approved-readable': '#4ADE80',
  '--status-changes-readable': '#FDBA74',
  '--ai-text': '#C4B5FD',
  '--status-rejected-text': '#FCA5A5',
  // These two hold the SAME hex as light and are listed anyway, because in dark that is a real
  // difference rather than a no-op: `--switch-checked` would otherwise derive from the dark
  // `--primary` (#FFFFFF) and put a white track under a lime thumb at 1.16, and
  // `--chart-boundary` is the separator that `--chart-1` can no longer be, dark having moved
  // series 1 to #FFFFFF. Pinning both to black is the point of declaring them. B1 wired the
  // boundary's consumer: `chart-theme.ts`'s `CHART_BOUNDARY_COLOR` reads it, so it is a rendered
  // token in both modes now rather than the declared-and-absent role A2a filed.
  '--switch-checked': '#000000',
  '--chart-boundary': '#000000',
  // The canvas ink is the one chart band that DOES invert: its ground is the card, so on a
  // #1A1A1A card it has to be white for the same reason the boundary has to stay black on a
  // series fill. This is the gate finding B1's first pass produced by giving one token both jobs.
  '--chart-ink': '#FFFFFF',
  // The STOCK RESOLUTION of a derived token, not an authored value: in dark the boundary is the
  // resolved `--input`, whose dark stock is #858585. It is listed because `STOCK_COLOURS.dark`
  // has to be what `resolveTheme('dark', {})` returns — `theme.test.ts`'s "returns stock for an
  // empty (reset) record" is the assertion that keeps the two in step, and it is what caught
  // this entry missing. The light arm needs no counterpart: there the boundary resolves to
  // `--primary`, whose light stock is the #000000 already in `STOCK_LIGHT`.
  '--switch-checked-boundary': '#858585',
} as const satisfies Partial<Record<ThemeTokenId, HexColour>>;

export const STOCK_COLOURS: Readonly<Record<ThemeMode, ResolvedTheme>> = Object.freeze({
  light: Object.freeze({ ...STOCK_LIGHT }) as ResolvedTheme,
  dark: Object.freeze({ ...STOCK_LIGHT, ...STOCK_DARK_DIFFERENCES }) as ResolvedTheme,
});

export const THEME_TOKEN_IDS = Object.keys(STOCK_LIGHT) as ThemeTokenId[];

const TRIPLET_TOKEN_SET: ReadonlySet<string> = new Set<string>(TRIPLET_TOKEN_NAMES);

export const THEME_TOKEN_SHAPES: Readonly<Record<ThemeTokenId, ThemeTokenShape>> = Object.freeze(
  Object.fromEntries(
    THEME_TOKEN_IDS.map((token) => [token, TRIPLET_TOKEN_SET.has(token) ? 'triplet' : 'hex-pair']),
  ) as Record<ThemeTokenId, ThemeTokenShape>,
);

// ─── The editable role catalogue ─────────────────────────────────────────────

export type ThemeRoleDefinition = {
  id: ThemeRoleId;
  label: string;
  group: 'Accents' | 'Controls' | 'Highlight' | 'Neutrals';
  writes: readonly { token: ThemeTokenId; shape: ThemeTokenShape }[];
  stock: Readonly<Record<ThemeMode, HexColour>>;
  /**
   * `rendered` — has at least one measured contrast pair.
   * `decorative` — a real consumer, but a floorless hairline / scrollbar role.
   * `non-rendered` — no consumer at all in `apps/web/src` (asserted absent by the web guards).
   *
   * Values mirror `apps/web/src/styles/__tests__/contrast-classification.ts`'s `kind`:
   * `--border`/`--brand-border`/`--brand-border-soft`/`--text-muted` are `decorative` there,
   * `--brand-border-hover`/`--text-secondary` are `non-rendered`.
   */
  usage: 'rendered' | 'decorative' | 'non-rendered';
};

function writes(...tokens: ThemeTokenId[]): { token: ThemeTokenId; shape: ThemeTokenShape }[] {
  return tokens.map((token) => ({ token, shape: THEME_TOKEN_SHAPES[token] }));
}

function stockOf(token: ThemeTokenId): Readonly<Record<ThemeMode, HexColour>> {
  return Object.freeze({
    light: STOCK_COLOURS.light[token],
    dark: STOCK_COLOURS.dark[token],
  });
}

/**
 * The 24 roles, in the plan's own table order: id, label, group, the CSS tokens the role
 * writes (first one carries the role's stock value), usage classification.
 *
 * Written as a table rather than 24 object literals so the code reads like the specification
 * it implements and a reviewer can diff it against the plan row by row.
 */
type RoleRow = readonly [
  ThemeRoleId,
  string,
  ThemeRoleDefinition['group'],
  readonly [ThemeTokenId, ...ThemeTokenId[]],
  ThemeRoleDefinition['usage'],
];

const ROLE_ROWS: readonly RoleRow[] = [
  ['accent-admin', 'Admin accent', 'Accents', ['--ss-yellow'], 'rendered'],
  ['accent-client', 'Client accent', 'Accents', ['--ss-blue'], 'rendered'],
  ['accent-influencer', 'Creator accent', 'Accents', ['--ss-pink'], 'rendered'],
  ['accent-agent', 'Agent accent', 'Accents', ['--ss-purple'], 'rendered'],
  ['interactive', 'Interactive colour (buttons, focus ring)', 'Controls', ['--primary', '--ring'], 'rendered'], // prettier-ignore
  ['body-text', 'Body text', 'Controls', ['--foreground', '--card-foreground', '--popover-foreground'], 'rendered'], // prettier-ignore
  ['muted-text', 'Muted text', 'Controls', ['--muted-foreground'], 'rendered'],
  ['raised-surface', 'Raised surface', 'Controls', ['--secondary'], 'rendered'],
  ['destructive', 'Destructive', 'Controls', ['--destructive'], 'rendered'],
  // `--border` is the DECORATIVE hairline, floorless and never a control boundary; the 3:1
  // boundary role is `control-boundary` (`--input`). Same split as the branded shell's.
  ['hairline', 'Hairline (decorative divider, never a control boundary)', 'Controls', ['--border'], 'decorative'], // prettier-ignore
  ['control-boundary', 'Control boundary', 'Controls', ['--input'], 'rendered'],
  ['highlight', 'Highlight', 'Highlight', ['--highlight'], 'rendered'],
  ['page-canvas', 'Page canvas', 'Neutrals', ['--bg-light', '--background'], 'rendered'],
  ['card-surface', 'Card surface', 'Neutrals', ['--bg-card', '--card', '--popover'], 'rendered'],
  ['chrome', 'Application chrome', 'Neutrals', ['--ss-black'], 'rendered'],
  ['chrome-gradient-end', 'Application chrome gradient end', 'Neutrals', ['--chrome-gradient-end'], 'rendered'], // prettier-ignore
  ['on-chrome', 'Text on application chrome', 'Neutrals', ['--on-chrome'], 'rendered'],
  ['hairline-strong', 'Strong hairline (decorative)', 'Neutrals', ['--brand-border'], 'decorative'], // prettier-ignore
  ['hairline-hover', 'Hairline hover (declared, no consumer in src/)', 'Neutrals', ['--brand-border-hover'], 'non-rendered'], // prettier-ignore
  ['hairline-soft', 'Soft hairline (decorative)', 'Neutrals', ['--brand-border-soft'], 'decorative'], // prettier-ignore
  ['text-strong', 'Strong text', 'Neutrals', ['--text-primary'], 'rendered'],
  ['text-secondary', 'Secondary text (declared, no consumer in src/)', 'Neutrals', ['--text-secondary'], 'non-rendered'], // prettier-ignore
  ['text-decorative', 'Decorative text tint (scrollbar thumb only)', 'Neutrals', ['--text-muted'], 'decorative'], // prettier-ignore
  ['content-secondary', 'Secondary content text', 'Neutrals', ['--content-secondary'], 'rendered'], // prettier-ignore
];

export const THEME_ROLES: readonly ThemeRoleDefinition[] = Object.freeze(
  ROLE_ROWS.map(([id, label, group, tokens, usage]) => ({
    id,
    label,
    group,
    writes: Object.freeze(writes(...tokens)),
    stock: stockOf(tokens[0]),
    usage,
  })),
);

export const THEME_ROLE_IDS: readonly ThemeRoleId[] = Object.freeze(
  THEME_ROLES.map((role) => role.id),
);

/**
 * theme-customization A2a — DERIVED code-owned tokens: token -> the tokens its resolved value
 * is computed FROM, in `resolveTheme`.
 *
 * These are not authored colours. The switch's checked track and its boundary follow the
 * resolved `--primary` / `--input`, so an admin editing the `interactive` or `control-boundary`
 * role still moves the control — which is exactly why the split is safe. Recording the
 * derivation as DATA rather than only as code is what keeps a validation failure on one of
 * those pairs attributable: `rolesFor` expands a derived leaf to its sources, so the editor can
 * still link the failure to the input that caused it. Without this the pair would be measured
 * correctly and reported with an empty `roleIds`.
 */
export const DERIVED_TOKEN_SOURCES: Readonly<
  Partial<Record<ThemeTokenId, readonly ThemeTokenId[]>>
> = Object.freeze({
  '--switch-checked': Object.freeze(['--primary'] as const),
  '--switch-checked-boundary': Object.freeze(['--primary', '--input'] as const),
});

/** token -> the single editable role that writes it. Code-owned tokens are absent. */
export const TOKEN_OWNER_ROLE: Readonly<Partial<Record<ThemeTokenId, ThemeRoleId>>> = Object.freeze(
  (() => {
    const map: Partial<Record<ThemeTokenId, ThemeRoleId>> = {};
    for (const role of THEME_ROLES) {
      for (const write of role.writes) {
        if (map[write.token]) {
          throw new Error(`${write.token} is written by two roles — the catalogue is ambiguous`);
        }
        map[write.token] = role.id;
      }
    }
    return map;
  })(),
);
